import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShopify, FaWordpress } from 'react-icons/fa';
import { Globe, Plug, CheckCircle2, ArrowRight, Zap, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SHOPIFY_INSTALL_URL } from '@/components/Navbar';

const IntegrationsPage = () => {
  const platforms = [
    {
      name: 'Shopify',
      icon: FaShopify,
      status: 'Deep Native Integration',
      isPrimary: true,
      color: '#95BF47',
      bg: 'bg-emerald-50 border-emerald-200',
      description: 'One-click install from Shopify App Store with automatic product sync, order tracking, and customer data integration.',
      features: ['Real-time catalog sync', 'Order tracking', 'Customer data sync', 'Analytics dashboard']
    },
    {
      name: 'WooCommerce',
      icon: FaWordpress,
      status: 'Supported',
      isPrimary: false,
      color: '#7B68A6',
      bg: 'bg-purple-50 border-purple-200',
      description: 'Connect your WooCommerce store via WordPress plugin for AI-powered customer engagement.',
      features: ['WordPress plugin', 'Product sync', 'Order management', 'Chat history']
    },
    {
      name: 'Custom Stores',
      icon: Globe,
      status: 'Via API',
      isPrimary: false,
      color: '#0D9488',
      bg: 'bg-teal-50 border-teal-200',
      description: 'Build custom integrations for any e-commerce platform using our REST API.',
      features: ['RESTful API', 'Webhook support', 'Custom mapping', 'Documentation']
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="integrations-page">
      <Navbar />
      
      <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
        {/* Hero with Image */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mb-16 sm:mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6">
                <Plug className="w-4 h-4 text-teal-600" />
                <span className="text-xs sm:text-sm font-bold text-teal-700">Platform Integration</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-4 sm:mb-6 leading-tight">
                Works With Your{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-500">E-commerce Platform</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed">
                Native Shopify integration with support for all major e-commerce platforms.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <img 
                src="/assets/integrations.webp"
                alt="Business team discussing e-commerce platform integrations"
                className="rounded-2xl sm:rounded-3xl shadow-xl w-full h-[260px] sm:h-[360px] object-cover border-2 border-slate-100"
                data-testid="integrations-hero-image"
              />
              <motion.div 
                className="absolute -bottom-4 -left-4 bg-emerald-600 text-white px-4 py-2 rounded-xl shadow-lg text-sm font-bold flex items-center gap-2"
                animate={{ y: [0, -4, 0] }}
                transition={{ repeat: Infinity, duration: 2.5 }}
              >
                <Plug className="w-4 h-4" />
                4+ Platforms
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Platforms */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mb-16 sm:mb-24">
          <div className="space-y-4 sm:space-y-6">
            {platforms.map((platform, index) => (
              <motion.div
                key={platform.name}
                className={`rounded-2xl sm:rounded-3xl overflow-hidden bg-white border-2 ${platform.bg} shadow-sm hover:shadow-lg transition-all duration-300`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`integration-card-${platform.name.toLowerCase()}`}
              >
                <div className="p-6 sm:p-8 md:p-10 lg:p-12">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center">
                    <div>
                      <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                        <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center bg-white border-2 border-slate-100 shadow-sm">
                          <platform.icon className="w-8 sm:w-10 h-8 sm:h-10" style={{ color: platform.color }} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{platform.name}</h2>
                            {platform.isPrimary && (
                              <span className="bg-teal-600 text-white text-xs font-bold px-2 sm:px-3 py-1 rounded-full">Primary</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <CheckCircle2 className={`w-4 h-4 ${platform.isPrimary ? 'text-teal-600' : 'text-slate-400'}`} />
                            <span className={`text-xs sm:text-sm ${platform.isPrimary ? 'text-teal-600 font-medium' : 'text-slate-500'}`}>
                              {platform.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-slate-600 leading-relaxed text-sm sm:text-base mb-4 sm:mb-6">{platform.description}</p>
                      
                      {platform.isPrimary && (
                        <a href={SHOPIFY_INSTALL_URL} target="_blank" rel="noopener noreferrer">
                          <Button className="bg-teal-600 hover:bg-teal-700 text-white group font-bold text-sm sm:text-base" data-testid="integrations-install-btn">
                            <Cpu className="w-4 h-4 mr-2" />
                            Install on Shopify
                            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </a>
                      )}
                    </div>
                    
                    <div className="rounded-xl sm:rounded-2xl p-4 sm:p-6 bg-slate-50 border border-slate-200">
                      <h3 className="font-semibold text-slate-900 mb-3 sm:mb-4 text-sm sm:text-base">Integration Features</h3>
                      <ul className="space-y-2 sm:space-y-3">
                        {platform.features.map((feature) => (
                          <li key={feature} className="flex items-center gap-2 sm:gap-3 text-slate-600">
                            <div className={`w-5 sm:w-6 h-5 sm:h-6 rounded-full ${platform.isPrimary ? 'bg-teal-100' : 'bg-slate-100'} flex items-center justify-center flex-shrink-0`}>
                              <Zap className={`w-2.5 sm:w-3 h-2.5 sm:h-3 ${platform.isPrimary ? 'text-teal-600' : 'text-slate-500'}`} />
                            </div>
                            <span className="text-xs sm:text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Need Help */}
        <section className="bg-gradient-to-r from-teal-600 to-emerald-600 py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-4 sm:mb-6">
                Need a Custom Integration?
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-teal-100 mb-6 sm:mb-8 px-4">
                Our team can build custom AI integrations for any e-commerce platform.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 group font-bold text-sm sm:text-base" data-testid="integrations-contact-btn">
                  Contact Our Team
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default IntegrationsPage;
